import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, combineLatest, fromEvent, map, Observable, Subscription, take, tap, startWith } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { TranslatedBenefitRow } from '@fcom/dapi/api/models';
import { TagRoundingStyle, TagTheme } from '@fcom/ui-components';
import { ConfigService, unsubscribe } from '@fcom/core/index';
import { MediaQueryService } from '@fcom/common/services';
import { FinnairThemeService } from '@fcom/common';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { shouldDisplayBenefit } from '../../../utils/benefit.utils';
import { ExtendedFareFamily, FareFamilyBadgeType } from '../../../interfaces';
import { TaggableComponent } from '../../../../ui-components/components/tag/taggable.component';

@Component({
  selector: 'fin-fare-family-list-option',
  styleUrls: ['./fare-family-list-option.component.scss'],
  templateUrl: './fare-family-list-option.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlightSelectionFareFamilyListOptionComponent extends TaggableComponent implements OnInit, OnDestroy {
  configService = inject(ConfigService);
  mediaQueryService = inject(MediaQueryService);
  private el = inject(ElementRef);
  private themeService = inject(FinnairThemeService);

  destroyRef = inject(DestroyRef);

  readonly FareFamilyBadgeType = FareFamilyBadgeType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TagRoundingStyle = TagRoundingStyle;
  readonly TagTheme = TagTheme;

  @Input({ required: true }) fareFamily$: Observable<ExtendedFareFamily>;
  @Input({ required: true }) currencyCode: string;
  @Input({ required: true }) aria: {
    index: number;
    count: number;
  };

  @Input() isShowTotalPrice = false;
  @Input() showKoreaNotification: boolean;

  @Output() selectFareFamily = new EventEmitter<boolean>();

  selected$: Observable<boolean> = this.selectFareFamily.asObservable().pipe(startWith(false));

  subscriptions = new Subscription();
  fareFamilyBenefit$: Observable<TranslatedBenefitRow[]>;
  isNotAward$: Observable<boolean>;
  isHover$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  tagTheme$: Observable<TagTheme>;

  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);
  readonly aviosTagTheme = computed(() => (this.isNewBrand() ? TagTheme.SEMI_PROMOTION : TagTheme.DEFAULT));

  ngOnInit(): void {
    fromEvent(this.el.nativeElement, 'mouseenter')
      .pipe(
        tap(() => this.isHover$.next(true)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    fromEvent(this.el.nativeElement, 'mouseleave')
      .pipe(
        tap(() => this.isHover$.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.isNotAward$ = this.fareFamily$.pipe(
      map((fareFamily) => {
        const isAward = Boolean(fareFamily.points);
        return !isAward;
      })
    );

    this.fareFamilyBenefit$ = this.fareFamily$.pipe(
      map((fareFamily) => {
        return fareFamily.benefits.translatedBenefitRows.filter((benefit) => shouldDisplayBenefit(benefit));
      })
    );

    this.tagTheme$ = combineLatest([this.isHover$, this.selected$, this.fareFamily$]).pipe(
      map(([isHover, selected, fareFamily]) => {
        if (selected) {
          return TagTheme.DEFAULT;
        }

        if (isHover) {
          return TagTheme.DEFAULT;
        }

        return fareFamily.benefits.tags[0].theme as TagTheme;
      }),
      startWith(TagTheme.ACTIVE)
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  checkboxChange(event: Event): void {
    this.selectFareFamily.emit((event.target as HTMLInputElement).checked);

    this.subscriptions.add(
      this.mediaQueryService
        .isBreakpoint$('tablet_down')
        .pipe(take(1))
        .subscribe((isTabletDown) => {
          if (!isTabletDown) {
            return;
          }

          const fareFamilyElement = (event.target as HTMLInputElement).closest('.fare-family');

          if (this.isHalfFareFamilyVisible(fareFamilyElement)) {
            fareFamilyElement.querySelector('label').scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            });
          }
        })
    );
  }

  private isHalfFareFamilyVisible(element: Element): boolean {
    const rect = element.getBoundingClientRect();
    return element.clientHeight / 2 < rect.top;
  }
}
